import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { CircleNotch, CaretDown, CaretUp } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_META = {
  known: { label: "Known", chip: "bg-emerald-700 text-white" },
  estimated: { label: "Estimated", chip: "bg-amber-500 text-white" },
  not_estimable: { label: "Not estimable", chip: "bg-red-700 text-white" },
};

function InputEditor({ row, meta, productId, onSaved, onCancel }) {
  const [draft, setDraft] = useState(() =>
    Object.fromEntries(meta.map((m) => {
      const e = row.inputs[m.key] || {};
      return [m.key, { value: e.value ?? "", basis: e.basis || "known" }];
    })));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const inputs = Object.fromEntries(Object.entries(draft).map(([k, e]) =>
        [k, { value: String(e.value).trim() === "" ? null : Number(e.value), basis: e.basis }]));
      await axios.put(`${API}/epidemiology/${productId}/${row.country_id}`, { inputs });
      toast.success(`${row.name} — epidemiology inputs saved`);
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save inputs — check the values.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`epi-editor-${row.country_id}`} className="bg-slate-50 border-t border-slate-200 px-5 py-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {meta.map((m) => (
          <div key={m.key}>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{m.label} <span className="normal-case">({m.unit})</span></p>
            <input data-testid={`epi-input-${row.country_id}-${m.key}`} value={draft[m.key].value}
              onChange={(e) => setDraft({ ...draft, [m.key]: { ...draft[m.key], value: e.target.value } })}
              placeholder="Not available"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 bg-white focus:outline-none focus:border-slate-500" />
            <div className="flex gap-1 mt-1">
              {["known", "estimated"].map((b) => (
                <button key={b} data-testid={`epi-basis-${row.country_id}-${m.key}-${b}`}
                  onClick={() => setDraft({ ...draft, [m.key]: { ...draft[m.key], basis: b } })}
                  className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 border ${draft[m.key].basis === b ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-400 border-slate-300"}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button data-testid={`epi-save-${row.country_id}`} disabled={busy} onClick={save}
          className="bg-slate-900 text-white text-xs font-semibold px-4 py-2 hover:bg-slate-800 disabled:opacity-50">
          {busy ? <CircleNotch size={13} className="animate-spin inline" /> : "Save inputs"}
        </button>
        <button onClick={onCancel} className="text-xs text-slate-500 border border-slate-300 px-4 py-2 hover:bg-white">Cancel</button>
        <p className="text-[10px] text-slate-400 ml-2">Leave a field empty when the figure is not available — it will read "Not estimable", never zero.</p>
      </div>
    </div>
  );
}

export default function EpidemiologyPage() {
  const { productId, product } = useProduct();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    axios.get(`${API}/epidemiology/${productId}`)
      .then((r) => { setD(r.data); setError(false); })
      .catch(() => setError(true));
  }, [productId]);

  useEffect(() => { setD(null); setEditing(null); load(); }, [load]);

  if (error) return (
    <PageShell pageId="epidemiology" overline="Epidemiology & Demand" title="Addressable Population">
      <p data-testid="epi-error" className="py-32 text-center text-xs text-red-600">Could not load epidemiology data. <button onClick={load} className="underline">Retry</button></p>
    </PageShell>
  );
  if (!d) return (
    <PageShell pageId="epidemiology" overline="Epidemiology & Demand" title="Addressable Population">
      <div data-testid="loading-state" className="py-32 text-center text-slate-400 text-sm">Loading epidemiology inputs…</div>
    </PageShell>
  );

  const s = d.summary;
  const rows = d.countries.filter((r) => filter === "all" || r.addressable.state === filter);

  return (
    <PageShell pageId="epidemiology" overline={`Epidemiology & Demand · ${product?.name || ""}`} title="Addressable Population">
      <div data-testid="epi-summary" className="grid grid-cols-2 md:grid-cols-4 bg-white border border-slate-200 divide-x divide-slate-200">
        {[["Markets", s.total, ""], ["Known", s.known, "text-emerald-700"],
          ["Estimated", s.estimated, "text-amber-600"],
          ["Not Estimable", s.not_estimable, s.not_estimable > 0 ? "text-red-700" : "text-emerald-700"]].map(([label, value, cls]) => (
          <div key={label} className="px-5 py-4">
            <p className="overline-label text-slate-400">{label}</p>
            <p className={`font-heading font-extrabold text-2xl mt-1 ${cls}`}>{value}</p>
          </div>
        ))}
      </div>

      <div data-testid="epi-filters" className="flex items-center gap-2">
        {[["all", "All"], ["known", "Known"], ["estimated", "Estimated"], ["not_estimable", "Not estimable"]].map(([k, label]) => (
          <button key={k} data-testid={`epi-filter-${k}`} onClick={() => setFilter(k)}
            className={`text-[11px] font-semibold px-3 py-1.5 border transition-colors ${filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      <section data-testid="epi-table" className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200">
          <p className="font-heading font-bold text-sm tracking-tight">Addressable Population Funnel</p>
          <p className="text-xs text-slate-500 mt-1">Population × prevalence × severity × symptom rate × eligibility, split by treatment penetration. All six inputs are required — a missing input renders "Not estimable" and is never converted to zero patients.</p>
        </div>
        {rows.length === 0 ? (
          <p data-testid="epi-empty" className="px-5 py-10 text-center text-xs text-slate-400">No markets match this filter.</p>
        ) : rows.map((r) => {
          const st = STATE_META[r.addressable.state];
          const open = editing === r.country_id;
          return (
            <div key={r.country_id} data-testid={`epi-row-${r.country_id}`} className="border-b border-slate-100 last:border-0">
              <div className="px-5 py-3 flex items-center gap-4">
                <span className="text-lg shrink-0">{r.flag}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800">{r.name}
                    <span data-testid={`epi-state-${r.country_id}`} className={`ml-2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${st.chip}`}>{st.label}</span>
                  </p>
                  {r.addressable.state === "not_estimable" ? (
                    <p data-testid={`epi-missing-${r.country_id}`} className="text-[10px] text-red-600 mt-0.5">
                      Missing inputs: {r.addressable.missing_inputs.join(", ") || r.addressable.reason}
                    </p>
                  ) : (
                    <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">
                      Treated ~{r.addressable.treated?.toLocaleString()} · untreated opportunity ~{r.addressable.untreated?.toLocaleString()}
                      {r.updated_by && ` · updated by ${r.updated_by}`}
                    </p>
                  )}
                </div>
                <p data-testid={`epi-value-${r.country_id}`} className={`font-mono-data text-sm font-bold shrink-0 ${r.addressable.state === "not_estimable" ? "text-red-700" : ""}`}>
                  {r.addressable.state === "not_estimable" ? "Not estimable" : `${r.addressable.display} patients`}
                </p>
                <button data-testid={`epi-edit-${r.country_id}`} onClick={() => setEditing(open ? null : r.country_id)}
                  className="text-[11px] font-semibold px-3 py-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center gap-1 shrink-0">
                  Edit inputs {open ? <CaretUp size={11} /> : <CaretDown size={11} />}
                </button>
              </div>
              {open && <InputEditor row={r} meta={d.required_inputs} productId={productId}
                onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
            </div>
          );
        })}
      </section>
    </PageShell>
  );
}
